/**
 * WorkforceBranchCoverage — the branch manager's single SERVICES surface: what the branch offers,
 * who can deliver it, and where the gaps are. One rail item, three tabs (operator, 2026-08-04:
 * "move it into the Service Coverage page as a tab… it should not be 2 separate rail items").
 * (operator, 2026-08-03: "an additional screen under Services that summarizes each staff
 * member and their LOB and what each staff member is assigned for services to determine
 * any gaps… the Branch Manager should also be able to configure on behalf of the staff,
 * and when they update a staff it should be reflected on that staff").
 *
 * Two readings of the same data, deliberately:
 *  · BY STAFF — each person, their line of business, and the services they are signed off
 *    to deliver. This is the configuring view.
 *  · BY SERVICE — every service the branch offers and how many people can deliver it. This
 *    is the GAP view, and it is the reason the screen exists: a service with nobody signed
 *    off is a booking the branch cannot honour, and it sorts to the top.
 *
 * Writes go through `setAdvisorServices`, the same override the catalogue reads, so a change
 * here appears on that person's own My Skills & Services immediately — no second store.
 * Line-of-business assignment stays Operations' call (`assignedBy` says which), so the
 * manager configures capability inside a line she does not own.
 *
 * Ops: getServiceCatalogue · setAdvisorServices.
 */
import { useEffect, useMemo, useState } from "react";
import { Tab, Btn, Icon, Pill, T, alpha } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { Drawer } from "../../shell/Overlay";
import { toast } from "../../shell/toast";
import { useBusy } from "./useWorkforce";
import { getServiceCatalogue, setAdvisorServices, getMyServiceScope, type ServiceCatalogue, type CatalogueAdvisor, type CatalogueService } from "./wf-api";
import { ServiceDetail } from "./WorkforceServiceCatalogue";
import { useT } from "../../lib/i18n";
import { useTenantLabels } from "../../lib/labels";

const mono = { fontFamily: T.fontMono };
const card = { border: `1px solid ${T.border}`, borderRadius: 12, background: T.white, overflow: "hidden" as const, flexShrink: 0 };
const cap = { fontSize: 11, color: T.soft, textTransform: "uppercase" as const, letterSpacing: 0.05, fontWeight: 700 };

/** Who a staff member may be signed off for: only services inside their line of business. */
function eligibleFor(a: CatalogueAdvisor, services: CatalogueService[]): CatalogueService[] {
  return services.filter((s) => a.lobs.includes(s.lob));
}

function CoverageEditor({ advisor, services, lobName, busy, onSave, onClose }: {
  advisor: CatalogueAdvisor; services: CatalogueService[]; lobName: (id: string) => string;
  busy: string | null; onSave: (advisor: string, next: string[]) => void; onClose: () => void;
}) {
  const t = useT();
  const [picked, setPicked] = useState<Set<string>>(new Set(advisor.services ?? []));
  const eligible = eligibleFor(advisor, services);
  const dirty = useMemo(() => {
    const was = new Set(advisor.services ?? []);
    if (was.size !== picked.size) return true;
    for (const id of picked) if (!was.has(id)) return true;
    return false;
  }, [advisor.services, picked]);
  const toggle = (id: string) => setPicked((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const byLob = useMemo(() => {
    const m = new Map<string, CatalogueService[]>();
    eligible.forEach((s) => { const arr = m.get(s.lob) ?? []; arr.push(s); m.set(s.lob, arr); });
    return [...m.entries()];
  }, [eligible]);

  return (
    <Drawer panel="coverage" width={560} testid="wfcov-editor" onClose={onClose}
      title={advisor.name} sub={`${advisor.role} · ${advisor.lobs.map(lobName).join(" · ") || t("workforce.coverage.noLine", "no line of business")}`}>
      <div style={{ padding: "16px 22px 26px", display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", gap: 9, alignItems: "flex-start", padding: "11px 13px", borderRadius: 8, background: alpha(T.n1, 3), border: `1px solid ${T.n6}` }}>
          <Icon.info size={13} />
          <span style={{ fontSize: 11.5, color: T.n2, lineHeight: 1.5 }}>
            {t("workforce.coverage.editorNote", "You're signing off what this person can be booked for. It appears on their own Services screen straight away. Their line of business is set by Operations — only services inside it can be assigned.")}
          </span>
        </div>

        {advisor.lobs.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", fontSize: 13, color: T.soft }}>
            {t("workforce.coverage.noLineBody", "This person has no line of business yet, so there is nothing to assign. Operations sets that.")}
          </div>
        ) : byLob.map(([lob, rows]) => (
          <div key={lob} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <span style={{ ...cap, fontSize: 10 }}>{lobName(lob)} · {rows.filter((s) => picked.has(s.id)).length}/{rows.length}</span>
            <div style={{ display: "flex", flexDirection: "column", border: `1px solid ${T.n6}`, borderRadius: 8, overflow: "hidden" }}>
              {rows.map((s, i) => {
                const on = picked.has(s.id);
                return (
                  /* ui-raw: service toggle row with a checkbox glyph — Checkbox/ToggleRow's pattern (group 4) */
                  <button key={s.id} type="button" data-testid="wfcov-service-toggle" data-id={s.id} aria-pressed={on} onClick={() => toggle(s.id)}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", border: "none", borderBottom: i === rows.length - 1 ? "none" : `1px solid ${T.n6}`, background: on ? alpha(T.p1, 5) : T.white, cursor: "pointer", textAlign: "left", fontFamily: T.font }}>
                    <span style={{ width: 14, height: 14, borderRadius: 3, border: `1.5px solid ${on ? T.p1 : T.border}`, background: on ? T.p1 : T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {on ? <Icon.check size={9} color={T.white} /> : null}
                    </span>
                    <span style={{ flex: 1, fontSize: 13, color: T.n1, fontWeight: on ? 600 : 500 }}>{s.name}</span>
                    <span style={{ fontSize: 11.5, color: T.soft, ...mono }}>{t("common.units.minutesCompact", "{n}m").replace("{n}", String(s.durationMin))}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <div style={{ display: "flex", gap: 9, alignItems: "center", flexWrap: "wrap" }}>
          <Btn disabled={!dirty || busy !== null} testid="wfcov-save" onClick={() => onSave(advisor.name, [...picked])}>
            {busy === `sv:${advisor.name}` ? t("workforce.coverage.saving", "Saving…") : t("workforce.coverage.save", "Save assignments")}
          </Btn>
          <Btn variant="secondary" onClick={onClose}>{t("common.actions.cancel", "Cancel")}</Btn>
          {dirty ? <span style={{ fontSize: 11.5, color: T.soft }}>{picked.size} {t("workforce.coverage.selected", "selected")}</span> : null}
        </div>
      </div>
    </Drawer>
  );
}

export default function WorkforceBranchCoverage({ branch = null }: { branch?: string | null }) {
  const t = useT();
  const lbl = useTenantLabels(); // "branch"/"line of business" are tenant vocabulary
  const [data, setData] = useState<ServiceCatalogue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<"catalogue" | "staff" | "gaps">("catalogue"); // ui-local
  const [editing, setEditing] = useState<CatalogueAdvisor | null>(null); // ui-local
  const [detail, setDetail] = useState<CatalogueService | null>(null); // ui-local (read-only service)
  const [q, setQ] = useState(""); // ui-local (catalogue search)
  const { busy, run } = useBusy();

  /* WHOSE branch this is. The self-scoped op already resolves the signed-in persona to a branch;
     reusing it keeps one answer to that question instead of a second lookup that could disagree. */
  const [scope, setScope] = useState<string | null>(branch);
  useEffect(() => {
    if (branch) return;
    let live = true;
    getMyServiceScope()
      .then((m) => { if (live) setScope(m.locationName ?? null); })
      .catch(() => { if (live) setError("Couldn't work out which branch you manage."); });
    return () => { live = false; };
  }, [branch]);

  useEffect(() => {
    if (!scope) return;   // never read the network catalogue on a screen that grants edit rights
    let live = true;
    setLoading(true); setError(null);
    getServiceCatalogue(scope)
      .then((r) => { if (live) { setData(r); setLoading(false); } })
      .catch((e: unknown) => { if (live) { setError(e instanceof Error ? e.message : String(e)); setLoading(false); } });
    return () => { live = false; };
  }, [scope]);

  const lobName = (id: string) => data?.lobs.find((l) => l.id === id)?.name ?? id;
  const intentName = (id: string) => data?.intents.find((i) => i.id === id)?.name ?? id;
  const groupName = (id: string | null) => (id ? (data?.questionGroups ?? []).find((g) => g.id === id)?.name ?? id : null);
  const services = useMemo(() => (data?.services ?? []).filter((s) => !s.archived), [data]);
  /* Only staff who deliver appointments belong here — a greeter has no service list to gap. */
  const staff = useMemo(() => (data?.advisors ?? []).filter((a) => a.role !== "Greeter"), [data]);

  /* THE GAP VIEW: every service, and who can deliver it. Uncovered first — that is the whole
     point of the screen, so the ordering does the arguing rather than a warning banner. */
  const coverage = useMemo(() => {
    return services.map((s) => {
      const who = staff.filter((a) => (a.services ?? []).includes(s.id));
      const eligible = staff.filter((a) => a.lobs.includes(s.lob));
      return { svc: s, who, eligible };
    }).sort((a, b) => (a.who.length - b.who.length) || a.svc.name.localeCompare(b.svc.name));
  }, [services, staff]);

  const gaps = coverage.filter((c) => c.who.length === 0);
  const thin = coverage.filter((c) => c.who.length === 1);

  const save = (advisor: string, next: string[]) => void run(`sv:${advisor}`, async () => {
    try {
      await setAdvisorServices(advisor, next);
      /* Re-read SCOPED: the write returns the unscoped catalogue, and adopting that would silently
         widen this screen to the whole network mid-session. */
      setData(await getServiceCatalogue(scope));
      setEditing(null);
      toast({ tone: "success", title: t("workforce.coverage.saved", "Assignments updated"), subtitle: `${advisor} · ${next.length} ${t("workforce.coverage.services", "services")}` });
    } catch (e) {
      toast({ tone: "danger", title: t("workforce.coverage.couldntSave", "Couldn't save that"), subtitle: e instanceof Error ? e.message : String(e) });
    }
  });

  const tabBtn = (id: "catalogue" | "staff" | "gaps", label: string, badge?: number) => (
    <Tab key={id} active={view === id} count={badge} testid="wfcov-tab" onClick={() => setView(id)}>{label}</Tab>
  );

  return (
    <Screen rail="wf-svc-coverage">
      <PageHeader title={t("workforce.coverage.titleServices", "Services")}
        subtitle={[scope, t("workforce.coverage.subtitleServices", "What this branch offers, who on your team can deliver it, and where nobody is signed off.")].filter(Boolean).join(" · ")}
        breadcrumb={false} />
      <DataSourceStrip screen="wfcoverage" ops={["getServiceCatalogue", "setAdvisorServices"]} reqId="REQ-128" />

      <div style={{ display: "flex", gap: 18, padding: "0 24px", borderBottom: `1px solid ${T.n6}`, flexShrink: 0 }}>
        {tabBtn("catalogue", `${lbl("branch", "Branch")} ${t("workforce.coverage.servicesLower", "services")}`)}
        {tabBtn("staff", t("workforce.coverage.byStaff", "By staff member"))}
        {tabBtn("gaps", t("workforce.coverage.byService", "By service"), gaps.length)}
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 34px", display: "flex", flexDirection: "column", gap: 16 }}>
        {error ? <div style={{ padding: 40, textAlign: "center", color: T.soft }}>{t("workforce.coverage.couldntLoad", "Couldn't load coverage:")} {error}</div>
          : !scope || loading || !data ? <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>{t("common.status.loading", "Loading…")}</div>
          : (
            <>
              {/* The headline the manager acts on, stated once. */}
              <div style={{ ...card, padding: "16px 20px", display: "flex", gap: 34, flexWrap: "wrap" }}>
                {[
                  { l: t("workforce.coverage.staffOnDesk", "Staff"), v: String(staff.length), c: T.n1 },
                  { l: t("workforce.coverage.servicesOffered", "Services offered here"), v: String(services.length), c: T.n1 },
                  { l: t("workforce.coverage.nobodySignedOff", "Nobody signed off"), v: String(gaps.length), c: gaps.length ? T.red1 : T.green1 },
                  { l: t("workforce.coverage.onlyOnePerson", "Only one person"), v: String(thin.length), c: thin.length ? T.orange1 : T.n1 },
                ].map((k) => (
                  <span key={k.l} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ fontSize: 11, color: T.soft, fontWeight: 600 }}>{k.l}</span>
                    <span style={{ fontSize: 24, fontWeight: 700, color: k.c, ...mono }}>{k.v}</span>
                  </span>
                ))}
              </div>

              {view === "catalogue" ? (
                /* Read-only: the branch manager reads the catalogue, she does not configure it —
                   Operations owns the service definitions. Same rows Riley sees, no controls. */
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="wfcov-cat-search"
                    placeholder={t("workforce.coverage.searchServices", "Search services, intents, lines of business…")}
                    style={{ height: 34, width: 340, padding: "0 12px", borderRadius: 8, border: `1px solid ${T.border}`, fontSize: 13, fontFamily: T.font, color: T.n1, flexShrink: 0 }} />
                  {(() => {
                    const needle = q.trim().toLowerCase();
                    const groups = (data.lobs ?? []).map((l) => {
                      const rows = services.filter((sv) => sv.lob === l.id && (!needle || [sv.name, intentName(sv.intent), l.name].join(" ").toLowerCase().includes(needle)));
                      return { lob: l, rows };
                    }).filter((g) => g.rows.length);
                    if (!groups.length) return <div style={{ padding: 30, textAlign: "center", fontSize: 13, color: T.soft }}>{t("workforce.coverage.nothingMatches", "Nothing matches that search.")}</div>;
                    return groups.map((g) => (
                      <div key={g.lob.id} style={card} data-testid="wfcov-cat-group" data-id={g.lob.id}>
                        <div style={{ padding: "13px 20px", borderBottom: `1px solid ${T.n6}`, display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", background: alpha(T.n1, 2) }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{g.lob.name}</span>
                          <span style={{ ...cap, fontSize: 10.5 }}>{g.rows.length} {t("workforce.coverage.services", "services")}</span>
                        </div>
                        {g.rows.map((sv, si) => (
                          <div key={sv.id} data-testid="wfcov-cat-row" data-id={sv.id} onClick={() => setDetail(sv)}
                            style={{ display: "grid", gridTemplateColumns: "minmax(170px,1.5fr) minmax(120px,1fr) 84px 96px", gap: 12, alignItems: "center", padding: "12px 20px", borderBottom: si === g.rows.length - 1 ? "none" : `1px solid ${T.n6}`, cursor: "pointer", opacity: sv.bookableNow === false ? 0.5 : 1 }}>
                            <span style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                              {sv.imageUrl ? <img src={sv.imageUrl} alt="" style={{ width: 40, height: 24, objectFit: "cover", borderRadius: 4, flexShrink: 0, background: T.n6 }} /> : null}
                              <span style={{ fontSize: 13, fontWeight: 600, color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sv.name}</span>
                            </span>
                            <span style={{ fontSize: 11.5, color: T.soft, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{intentName(sv.intent)}</span>
                            <span style={{ fontSize: 12, color: T.n2, textAlign: "right", ...mono }}>{sv.durationMin}m</span>
                            <span style={{ textAlign: "right" }}>
                              {sv.bookableNow === false
                                ? <Pill tone="warn">{sv.unavailableReason === "before-window" ? `${t("workforce.coverage.fromDate", "from")} ${sv.disableBefore}` : t("workforce.coverage.ended", "ended")}</Pill>
                                : <span style={{ fontSize: 11.5, color: T.soft, ...mono }}>{sv.blockMin}m {t("workforce.coverage.block", "block")}</span>}
                            </span>
                          </div>
                        ))}
                      </div>
                    ));
                  })()}
                  <span style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.55 }}>
                    {t("workforce.coverage.readOnlyNote", "Service definitions are maintained by Operations. Tap a service for its length, channels, booking questions and rules.")}
                  </span>
                </div>
              ) : view === "staff" ? (
                <div style={card}>
                  <div style={{ padding: "13px 20px", borderBottom: `1px solid ${T.n6}`, display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline", flexWrap: "wrap" }}>
                    <span style={cap}>{t("workforce.coverage.yourTeam", "Your team")}</span>
                    <span style={{ fontSize: 11, color: T.soft }}>{t("workforce.coverage.tapToAssign", "Tap a person to change what they're signed off for")}</span>
                  </div>
                  {staff.map((a, i) => {
                    const assigned = (a.services ?? []).length;
                    const eligible = eligibleFor(a, services).length;
                    return (
                      <div key={a.name} data-testid="wfcov-staff-row" data-id={a.name} onClick={() => setEditing(a)}
                        style={{ display: "grid", gridTemplateColumns: "minmax(150px,1.2fr) minmax(140px,1fr) 120px 90px", gap: 12, alignItems: "center", padding: "14px 20px", borderBottom: i === staff.length - 1 ? "none" : `1px solid ${T.n6}`, cursor: "pointer" }}>
                        <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                          <span style={{ fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{a.name}</span>
                          <span style={{ fontSize: 11.5, color: T.soft }}>{a.role}</span>
                        </span>
                        <span style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                          {a.lobs.length === 0
                            ? <Pill tone="warn">{t("workforce.coverage.noLine", "no line of business")}</Pill>
                            : a.lobs.map((l) => <Pill key={l} tone="neutral">{lobName(l)}</Pill>)}
                        </span>
                        <span style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                          <span style={{ fontSize: 12.5, color: assigned === 0 ? T.red1 : T.n1, fontWeight: 600, ...mono }}>
                            {assigned} / {eligible} {t("workforce.coverage.svcShort", "services")}
                          </span>
                          <span style={{ height: 5, borderRadius: 3, background: T.n6, overflow: "hidden" }}>
                            <span style={{ display: "block", height: "100%", width: `${eligible ? (assigned / eligible) * 100 : 0}%`, background: assigned === 0 ? T.red1 : assigned === eligible ? T.green1 : T.p1 }} />
                          </span>
                        </span>
                        <span style={{ textAlign: "right" }}>
                          {a.servicesSource === "manager"
                            ? <Pill tone="primary">{t("workforce.coverage.setByYou", "Set here")}</Pill>
                            : <span style={{ fontSize: 11, color: T.soft }}>{t("workforce.coverage.fromSetup", "From setup")}</span>}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={card}>
                  <div style={{ padding: "13px 20px", borderBottom: `1px solid ${T.n6}`, display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline", flexWrap: "wrap" }}>
                    <span style={cap}>{t("workforce.coverage.everyServiceHere", "Every service this branch offers")}</span>
                    <span style={{ fontSize: 11, color: T.soft }}>{t("workforce.coverage.uncoveredFirst", "Uncovered first")}</span>
                  </div>
                  {coverage.map((c, i) => (
                    <div key={c.svc.id} data-testid="wfcov-service-row" data-id={c.svc.id}
                      style={{ display: "grid", gridTemplateColumns: "minmax(170px,1.3fr) minmax(150px,1fr) 108px", gap: 12, alignItems: "center", padding: "13px 20px", borderBottom: i === coverage.length - 1 ? "none" : `1px solid ${T.n6}`, background: c.who.length === 0 ? alpha(T.red1, 4) : T.white }}>
                      <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.svc.name}</span>
                        <span style={{ fontSize: 11, color: T.soft }}>{lobName(c.svc.lob)}</span>
                      </span>
                      <span style={{ fontSize: 12, color: c.who.length ? T.n2 : T.red1, fontWeight: c.who.length ? 400 : 700 }}>
                        {c.who.length
                          ? c.who.map((a) => a.name).join(", ")
                          : c.eligible.length
                            ? t("workforce.coverage.nobodyButEligible", "Nobody signed off — {n} could be").replace("{n}", String(c.eligible.length))
                            : t("workforce.coverage.nobodyInLine", "Nobody here works this line")}
                      </span>
                      <span style={{ textAlign: "right" }}>
                        {c.who.length === 0 ? <Pill tone="danger">{t("workforce.coverage.gap", "Gap")}</Pill>
                          : c.who.length === 1 ? <Pill tone="warn">{t("workforce.coverage.singlePoint", "One person")}</Pill>
                          : <span style={{ fontSize: 12, color: T.green1, fontWeight: 700, ...mono }}>{c.who.length}</span>}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
      </div>
      {/* `surveyName` and `couponName` are required and were missing (2026-09-08).
          This screen loads the coverage view, not the full catalogue, so it has
          no survey or coupon rows to resolve against — and rather than fake a
          lookup, both degrade the way `groupName` already does in the catalogue
          itself: return the ID. An id on screen is something an operator can
          search for and says plainly that the name was not resolved here, where
          "—" or "Unknown" would assert the field is empty. */}
      {detail ? (
        <ServiceDetail
          svc={detail}
          intentName={intentName(detail.intent)}
          lobName={lobName(detail.lob)}
          groupName={groupName}
          surveyName={(id: string | null) => id}
          couponName={(id: string) => id}
          onClose={() => setDetail(null)}
        />
      ) : null}
      {editing ? (
        <CoverageEditor advisor={editing} services={services} lobName={lobName} busy={busy} onSave={save} onClose={() => setEditing(null)} />
      ) : null}
    </Screen>
  );
}
